'use client'

import { Toggle } from '@base-ui/react/toggle'
import { Close } from '@carbon/icons-react'
import React from 'react'
import { twMerge } from 'tailwind-merge'

export type TagColor =
  | 'green'
  | 'blue'
  | 'purple'
  | 'yellow'
  | 'gray'
  | 'teal'
  | 'black'
  | 'tertiary'

export type TagSize = 'sm' | 'md' | 'lg'

const tagColorClasses: Record<TagColor, string> = {
  green: 'bg-case-tag-green-background text-case-tag-green-text',
  blue: 'bg-case-tag-blue-background text-case-tag-blue-text border-case-tag-blue-text',
  purple: 'bg-case-tag-purple-background text-case-tag-purple-text',
  yellow: 'bg-case-tag-yellow-background text-case-tag-yellow-text',
  teal: 'bg-tag-teal-background text-tag-teal-text border-tag-teal-text',
  black: 'bg-black text-white',
  tertiary: 'bg-tertiary',
  gray: 'bg-tag-gray-background text-tag-gray-text border-tag-gray-border',
}

const selectableTagColorClasses: Record<TagColor, string> = {
  green:
    'data-pressed:bg-case-tag-green-text data-pressed:text-case-tag-green-background',
  blue: 'data-pressed:bg-case-tag-blue-text data-pressed:text-case-tag-blue-background',
  purple:
    'data-pressed:bg-case-tag-purple-text data-pressed:text-case-tag-purple-background',
  yellow:
    'data-pressed:bg-case-tag-yellow-text data-pressed:text-case-tag-yellow-background',
  teal: 'data-pressed:bg-tag-teal-text data-pressed:text-tag-teal-background',
  black: 'data-pressed:bg-black data-pressed:text-white',
  tertiary:
    'data-pressed:bg-text-tertiary data-pressed:text-white hover:bg-tertiary-hover text-primary',
  gray: 'data-pressed:bg-tag-gray-text data-pressed:text-tag-gray-background',
}

export function getTagColorClasses(color?: TagColor): string {
  return tagColorClasses[color ?? 'gray']
}

const baseTagClassName =
  'inline-flex h-fit w-fit max-w-full min-w-0 items-center'

const tagSizeClasses: Record<TagSize, string> = {
  sm: 'label-01 rounded-xl px-2 py-px',
  md: 'label-01 rounded-full px-2 py-1',
  lg: 'label-01 rounded-full px-4 py-2',
}

function getTagClassName({
  color = 'gray',
  size = 'sm',
  interactive = false,
  disabled = false,
}: {
  color?: TagColor
  size?: TagSize
  interactive?: boolean
  disabled?: boolean
}) {
  return twMerge(
    baseTagClassName,
    tagColorClasses[color],
    tagSizeClasses[size],
    // larger touch target below the md breakpoint
    interactive &&
      'focus-visible:outline-focus cursor-pointer select-none focus-visible:outline-2 max-md:px-4 max-md:py-2',
    disabled && 'pointer-events-none opacity-35',
  )
}

type TagBaseProps = {
  color?: TagColor
  size?: TagSize
  /** Decorative leading icon. Hidden from assistive technology. */
  icon?: React.ReactNode
  className?: string
  style?: React.CSSProperties
  children: React.ReactNode
}

function TagIcon({ icon }: { icon: React.ReactNode }) {
  if (!icon) return null
  return (
    <span aria-hidden="true" className="mr-1 flex shrink-0 items-center">
      {icon}
    </span>
  )
}

// string children truncate with the full text on hover; constrain via max-w-* className
function TagLabel({ children }: { children: React.ReactNode }) {
  if (typeof children === 'string') {
    return (
      <span className="truncate" title={children}>
        {children}
      </span>
    )
  }
  return <>{children}</>
}

/**
 * Read-only tag for categorization, status indicators and metadata display.
 */
export default function Tag({
  color,
  size,
  icon,
  className,
  style,
  children,
}: TagBaseProps) {
  return (
    <span
      className={twMerge(getTagClassName({ color, size }), className)}
      style={style}
    >
      <TagIcon icon={icon} />
      <TagLabel>{children}</TagLabel>
    </span>
  )
}

function getDismissIconSizeClassName(size: TagSize) {
  switch (size) {
    case 'md':
    case 'lg':
      return 'size-4'
    case 'sm':
      return 'size-3'
  }
}

// vertical padding lives on the label so the dismiss button can span the full tag height
function getDismissContentPaddingClassName(size: TagSize) {
  switch (size) {
    case 'lg':
      return 'py-2'
    case 'md':
      return 'py-1'
    case 'sm':
      return 'py-px'
  }
}

// square buttons matching the tag height per size
function getDismissButtonSizeClassName(size: TagSize) {
  switch (size) {
    case 'lg':
      return 'size-8'
    case 'md':
      return 'size-6'
    case 'sm':
      return 'size-4.5'
  }
}

type DismissibleTagProps = TagBaseProps & {
  onDismiss: () => void
  /** Accessible label for the icon-only dismiss button. */
  dismissTitle: string
  disabled?: boolean
}

/**
 * Tag with a dismiss button, e.g. for removable filters.
 * Only the dismiss button is interactive, not the tag itself.
 */
export function DismissibleTag({
  onDismiss,
  dismissTitle,
  disabled,
  color,
  size = 'sm',
  icon,
  className,
  style,
  children,
}: DismissibleTagProps) {
  return (
    <span
      className={twMerge(
        getTagClassName({ color, size, disabled }),
        'py-0 pr-0',
        className,
      )}
      style={style}
    >
      <span
        className={`flex min-w-0 items-center ${getDismissContentPaddingClassName(size)}`}
      >
        <TagIcon icon={icon} />
        <TagLabel>{children}</TagLabel>
      </span>
      <button
        aria-label={dismissTitle}
        type="button"
        title={dismissTitle}
        disabled={disabled}
        onClick={onDismiss}
        className={`ml-0.5 flex shrink-0 cursor-pointer items-center justify-center rounded-full hover:bg-black/10 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-black ${getDismissButtonSizeClassName(size)}`}
      >
        <Close
          className={getDismissIconSizeClassName(size)}
          aria-hidden="true"
        />
      </button>
    </span>
  )
}

type SelectableTagProps = TagBaseProps & {
  selected?: boolean
  defaultSelected?: boolean
  onSelectedChange?: (selected: boolean) => void
  disabled?: boolean
}

/**
 * Toggleable tag for single or multi selection, e.g. filter facets.
 * Renders a button with `aria-pressed`.
 */
export function SelectableTag({
  selected,
  defaultSelected,
  onSelectedChange,
  disabled,
  color = 'gray',
  size = 'md',
  icon,
  className,
  style,
  children,
}: SelectableTagProps) {
  return (
    <Toggle
      pressed={selected}
      defaultPressed={defaultSelected}
      onPressedChange={(pressed) => onSelectedChange?.(pressed)}
      disabled={disabled}
      className={twMerge(
        getTagClassName({ color, size, interactive: true, disabled }),
        selectableTagColorClasses[color],
        className,
      )}
      style={style}
    >
      <TagIcon icon={icon} />
      <TagLabel>{children}</TagLabel>
    </Toggle>
  )
}

type OperationalTagProps = TagBaseProps & {
  onClick: () => void
  disabled?: boolean
}

/**
 * Tag that triggers an action, e.g. viewing a list of related items.
 * The border distinguishes it from read-only tags.
 */
export function OperationalTag({
  onClick,
  disabled,
  color,
  size = 'md',
  icon,
  className,
  style,
  children,
}: OperationalTagProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={twMerge(
        getTagClassName({ color, size, interactive: true, disabled }),
        'border hover:brightness-95',
        className,
      )}
      style={style}
    >
      <TagIcon icon={icon} />
      <TagLabel>{children}</TagLabel>
    </button>
  )
}
